import { Alert, Badge, Tab, Table, Tabs } from "react-bootstrap";
import styled from "styled-components";
import { AgencyTags, Card, JsonLog } from "@bagel-web/components";
import { FnDesc, FnParameter, SimpleParameter } from "../types";
import { AgencyTagFilter } from "../tag/AgencyTagFilter";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ShowArrayParam({ items }: { items: FnParameter }) {
  return (
    <>
      <div className="mt-3">
        <i>Array Elements: </i>
        {items.docs}
      </div>
      {items.value_type === "array" && <ShowArrayParam items={items.items} />}
      {items.value_type === "object" && (
        <ShowObjectParam properties={items.properties} />
      )}
    </>
  );
}

function ShowObjectParam({
  properties,
}: {
  properties: Record<string, FnParameter> | undefined | null;
}) {
  if (!properties || Object.keys(properties).length === 0) {
    return <div>No properties defined.</div>;
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>Property</th>
          <th>Type</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {Object.keys(properties).map((propName) => {
          return (
            <ShowFunctionParam
              key={propName}
              paramName={propName}
              paramDesc={properties[propName]!}
            />
          );
        })}
      </tbody>
    </Table>
  );
}

const RequiredBadge = styled(Badge)``;

const getDisplayValueType = (paramDesc: FnParameter) => {
  if (paramDesc.value_type === "array") {
    return `array[${paramDesc.items.value_type}]`;
  }
  return paramDesc.value_type;
};

const EnumValues = ({ paramDesc }: { paramDesc: SimpleParameter }) => {
  return (
    paramDesc.enumeration && (
      <div className="mt-3">
        <i>Valid values: </i>
        {paramDesc.enumeration.map((value, index) => (
          <span key={index}>
            <code key={index}>{value as string}</code>
            {index < (paramDesc.enumeration?.length || 0) - 1 && ", "}
          </span>
        ))}
      </div>
    )
  );
};

function ShowFunctionParam({
  paramName,
  paramDesc,
  required,
}: {
  paramName?: string;
  paramDesc: FnParameter;
  required?: boolean;
}) {
  const bottomBorderStyle = {
    borderBottom:
      paramDesc.value_type === "array" || paramDesc.value_type === "object"
        ? "hidden"
        : "",
  };

  return (
    <>
      <tr key={paramName}>
        <td
          rowSpan={
            paramDesc.value_type === "array" ||
            paramDesc.value_type === "object"
              ? 2
              : 1
          }
        >
          <code style={{ marginRight: "10px" }}>{paramName}</code>
          {required && <RequiredBadge bg="secondary">Required</RequiredBadge>}
        </td>
        <td style={bottomBorderStyle}>
          <code style={{ color: "var(--bs-text-primary)" }}>
            {getDisplayValueType(paramDesc)}
          </code>
        </td>
        <td style={bottomBorderStyle}>
          <div className="pre-wrap">{paramDesc.docs}</div>
          <div>
            {"enumeration" in paramDesc && <EnumValues paramDesc={paramDesc} />}
          </div>
        </td>
      </tr>
      {paramDesc.value_type === "array" && (
        <tr>
          <td colSpan={2}>
            <ShowArrayParam items={paramDesc.items} />
          </td>
        </tr>
      )}
      {paramDesc.value_type === "object" && (
        <tr>
          <td colSpan={2}>
            <ShowObjectParam properties={paramDesc.properties} />
          </td>
        </tr>
      )}
    </>
  );
}

const StyledTabs = styled(Tabs)`
  --bs-nav-link-color: var(--bs-body-color);
  --bs-nav-link-hover-color: var(--bs-body-color);
`;

function ShowFunctions({
  functions,
  asCards = false,
  agencyTagFilter,
}: {
  functions: Array<FnDesc>;
  asCards?: boolean;
  agencyTagFilter?: AgencyTagFilter;
}) {
  if (functions.length === 0) {
    return <Alert>No functions defined.</Alert>;
  }

  const content = functions
    .filter(
      (fn) => !agencyTagFilter || agencyTagFilter.matchAny(fn.agency_tags)
    )
    .map((fn) => {
      const funcContent = (
        <div data-testid="agency-function" className="mt-3" key={fn.name}>
          <div className="d-flex justify-content-between align-items-center flex-row gap-2 mb-2">
            <b>
              <code className="fs-5">{fn.name}</code>
            </b>
            <AgencyTags tags={fn.agency_tags} />
          </div>
          <div className="pre-wrap">{fn.docs}</div>
          {Object.keys(fn.parameters).length === 0 ? (
            "No arguments."
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <th>Argument</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(fn.parameters).map((paramName) => {
                    const paramDesc = fn.parameters[paramName]!;
                    return (
                      <ShowFunctionParam
                        key={paramName}
                        paramName={paramName}
                        paramDesc={paramDesc}
                        required={fn.required?.includes(paramName)}
                      />
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </div>
      );

      return asCards ? (
        <Card id={fn.name} key={fn.name}>
          <StyledTabs variant="underline">
            <Tab eventKey={`pretty-func-${fn.name}`} title="Pretty">
              {funcContent}
            </Tab>
            <Tab eventKey={`json-func-${fn.name}`} title="JSON">
              <div>
                <JsonLog id={`json-func-${fn.name}`} data={fn} />
              </div>
            </Tab>
          </StyledTabs>
        </Card>
      ) : (
        <li key={fn.name}>{funcContent}</li>
      );
    });

  return asCards ? content : <ul>{content}</ul>;
}

export default ShowFunctions;
